import React, { useState, useEffect } from 'react';
import {
  Save,
  RefreshCw,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  MessageSquare,
  Sparkles,
  BarChart3,
  Palette,
  Bot,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { settingsAPI, referenceAPI } from '../lib/api';
import { toast } from 'sonner';

function SettingSection({ title, description, children }) {
  return (
    <Card className="bg-surface-card border-border-default">
      <CardHeader>
        <CardTitle className="text-lg font-heading text-text-primary">{title}</CardTitle>
        {description && <CardDescription className="text-text-secondary">{description}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-4">
        {children}
      </CardContent>
    </Card>
  );
}

function ConnectionStatus({ connected, testing = false }) {
  if (testing) {
    return (
      <Badge className="bg-blue-50 text-blue-700 border border-blue-200">
        <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
        Testing...
      </Badge>
    );
  }

  return connected ? (
    <Badge className="bg-green-50 text-green-700 border border-green-200">
      <CheckCircle className="w-3 h-3 mr-1" />
      Connected
    </Badge>
  ) : (
    <Badge className="bg-red-50 text-red-700 border border-red-200">
      <XCircle className="w-3 h-3 mr-1" />
      Not Configured
    </Badge>
  );
}

export function Settings() {
  const [activeTab, setActiveTab] = useState('clicksend');
  
  // ClickSend State
  const [clicksendConfig, setClicksendConfig] = useState({
    username: '',
    api_key: '',
    default_sender_name: '',
    default_sender_number: '',
  });
  const [clicksendTesting, setClicksendTesting] = useState(false);
  const [clicksendConnected, setClicksendConnected] = useState(false);
  const [clicksendBalance, setClicksendBalance] = useState(null);
  const [showClicksendKey, setShowClicksendKey] = useState(false);
  
  // Gamma State
  const [gammaConfig, setGammaConfig] = useState({
    api_key: '',
    template_id: '',
  });
  const [gammaTesting, setGammaTesting] = useState(false);
  const [gammaConnected, setGammaConnected] = useState(false);
  const [showGammaKey, setShowGammaKey] = useState(false);
  
  // Grok State
  const [grokConfig, setGrokConfig] = useState({
    api_key: '',
    api_base_url: 'https://api.x.ai/v1',
    default_model: 'grok-3',
  });
  const [grokTesting, setGrokTesting] = useState(false);
  const [grokConnected, setGrokConnected] = useState(false);
  const [showGrokKey, setShowGrokKey] = useState(false);
  
  // Analytics State
  const [publications, setPublications] = useState([]);
  const [selectedPublication, setSelectedPublication] = useState('');
  const [analyticsConfig, setAnalyticsConfig] = useState({
    provider: '',
    matomo_url: '',
    matomo_site_id: '',
    matomo_auth_token: '',
    ga4_property_id: '',
  });
  const [analyticsTesting, setAnalyticsTesting] = useState(false);
  const [analyticsConnected, setAnalyticsConnected] = useState(false);

  useEffect(() => {
    loadConfigs();
    loadPublications();
  }, []);

  useEffect(() => {
    if (selectedPublication) {
      loadAnalyticsConfig(selectedPublication);
    }
  }, [selectedPublication]);

  const loadConfigs = async () => {
    try {
      const [clicksendRes, gammaRes] = await Promise.all([
        settingsAPI.getClickSend(),
        settingsAPI.getGamma(),
      ]);
      
      if (clicksendRes.data.is_configured) {
        setClicksendConfig({
          username: clicksendRes.data.username || '',
          api_key: clicksendRes.data.api_key || '',
          default_sender_name: clicksendRes.data.default_sender_name || '',
          default_sender_number: clicksendRes.data.default_sender_number || '',
        });
        setClicksendConnected(true);
      }
      
      if (gammaRes.data.is_configured) {
        setGammaConfig({
          api_key: gammaRes.data.api_key || '',
          template_id: gammaRes.data.template_id || '',
        });
        setGammaConnected(true);
      }
    } catch (error) {
      console.error('Failed to load configs:', error);
    }
  };

  const loadPublications = async () => {
    try {
      const response = await referenceAPI.getPublications();
      setPublications(response.data);
      if (response.data.length > 0) {
        setSelectedPublication(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load publications:', error);
    }
  };

  const loadAnalyticsConfig = async (publicationId) => {
    try {
      const response = await settingsAPI.getAnalytics(publicationId);
      if (response.data.is_configured) {
        setAnalyticsConfig({
          provider: response.data.provider || '',
          matomo_url: response.data.matomo_url || '',
          matomo_site_id: response.data.matomo_site_id || '',
          matomo_auth_token: '',
          ga4_property_id: response.data.ga4_property_id || '',
        });
        setAnalyticsConnected(true);
      } else {
        setAnalyticsConfig({
          provider: '',
          matomo_url: '',
          matomo_site_id: '',
          matomo_auth_token: '',
          ga4_property_id: '',
        });
        setAnalyticsConnected(false);
      }
    } catch (error) {
      console.error('Failed to load analytics config:', error);
    }
  };

  const handleSaveClicksend = async () => {
    try {
      await settingsAPI.saveClickSend(clicksendConfig);
      toast.success('ClickSend configuration saved');
      setClicksendConnected(true);
    } catch (error) {
      toast.error('Failed to save ClickSend configuration');
    }
  };

  const handleTestClicksend = async () => {
    try {
      setClicksendTesting(true);
      const response = await settingsAPI.testClickSend();
      if (response.data.success) {
        setClicksendConnected(true);
        setClicksendBalance(response.data.balance);
        toast.success(`Connection successful! Balance: ${response.data.balance} ${response.data.currency}`);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setClicksendTesting(false);
    }
  };

  const handleSaveGamma = async () => {
    try {
      await settingsAPI.saveGamma(gammaConfig);
      toast.success('Gamma configuration saved');
      setGammaConnected(true);
    } catch (error) {
      toast.error('Failed to save Gamma configuration');
    }
  };

  const handleTestGamma = async () => {
    try {
      setGammaTesting(true);
      const response = await settingsAPI.testGamma();
      if (response.data.success) {
        setGammaConnected(true);
        toast.success('Connection successful!');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setGammaTesting(false);
    }
  };

  const handleSaveGrok = async () => {
    try {
      await settingsAPI.saveGrok(grokConfig);
      toast.success('Grok configuration saved');
      setGrokConnected(true);
    } catch (error) {
      toast.error('Failed to save Grok configuration');
    }
  };

  const handleTestGrok = async () => {
    try {
      setGrokTesting(true);
      const response = await settingsAPI.testGrok();
      if (response.data.success) {
        setGrokConnected(true);
        toast.success('Connected — Grok API responding');
      } else {
        toast.error(response.data.message || 'Connection failed');
      }
    } catch (error) {
      toast.error('Connection test failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGrokTesting(false);
    }
  };

  const handleSaveAnalytics = async () => {
    if (!selectedPublication) {
      toast.error('Please select a publication');
      return;
    }
    
    try {
      await settingsAPI.saveAnalytics({
        publication_id: selectedPublication,
        ...analyticsConfig,
      });
      toast.success('Analytics configuration saved');
      setAnalyticsConnected(true);
    } catch (error) {
      toast.error('Failed to save analytics configuration');
    }
  };

  const handleTestAnalytics = async () => {
    if (!selectedPublication) return;
    
    try {
      setAnalyticsTesting(true);
      const response = await settingsAPI.testAnalytics(selectedPublication);
      if (response.data.success) {
        setAnalyticsConnected(true);
        toast.success(`Connection successful! Provider: ${response.data.provider}`);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setAnalyticsTesting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-text-primary uppercase tracking-tight">
          Settings
        </h1>
        <p className="text-text-secondary mt-1">Configure integrations and system settings</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 border border-slate-200">
          <TabsTrigger value="clicksend" className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            ClickSend
          </TabsTrigger>
          <TabsTrigger value="gamma" className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Gamma AI
          </TabsTrigger>
          <TabsTrigger value="grok" className="flex items-center gap-2">
            <Bot className="w-4 h-4" />
            Grok AI
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="branding" className="flex items-center gap-2">
            <Palette className="w-4 h-4" />
            Branding
          </TabsTrigger>
        </TabsList>

        {/* ClickSend Tab */}
        <TabsContent value="clicksend" className="space-y-6 mt-6">
          <SettingSection
            title="ClickSend Configuration"
            description="Configure SMS and Email sending via ClickSend API"
          >
            <div className="flex items-center justify-between mb-4">
              <ConnectionStatus connected={clicksendConnected} testing={clicksendTesting} />
              {clicksendBalance !== null && (
                <Badge variant="outline">Balance: ${clicksendBalance.toFixed(2)}</Badge>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Username (Email)</Label>
                <Input
                  value={clicksendConfig.username}
                  onChange={(e) => setClicksendConfig({...clicksendConfig, username: e.target.value})}
                  className="bg-bg-tertiary border-border-default"
                  data-testid="clicksend-username"
                />
              </div>
              <div>
                <Label>API Key</Label>
                <div className="relative">
                  <Input
                    type={showClicksendKey ? 'text' : 'password'}
                    value={clicksendConfig.api_key}
                    onChange={(e) => setClicksendConfig({...clicksendConfig, api_key: e.target.value})}
                    className="bg-bg-tertiary border-border-default pr-10"
                    data-testid="clicksend-api-key"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0"
                    onClick={() => setShowClicksendKey(!showClicksendKey)}
                  >
                    {showClicksendKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Default Sender Name</Label>
                <Input
                  value={clicksendConfig.default_sender_name}
                  onChange={(e) => setClicksendConfig({...clicksendConfig, default_sender_name: e.target.value})}
                  placeholder="QuantumCast"
                  className="bg-bg-tertiary border-border-default"
                />
              </div>
              <div>
                <Label>Default Sender Number</Label>
                <Input
                  value={clicksendConfig.default_sender_number}
                  onChange={(e) => setClicksendConfig({...clicksendConfig, default_sender_number: e.target.value})}
                  placeholder="+44..."
                  className="bg-bg-tertiary border-border-default"
                />
              </div>
            </div>

            <Separator className="my-4" />

            <div className="bg-slate-50 border border-slate-200 p-4 rounded-lg">
              <p className="text-sm text-text-secondary">
                <span className="text-text-tertiary">Webhook URL:</span>{' '}
                <code className="text-accent-primary">{window.location.origin}/api/webhooks/clicksend</code>
              </p>
              <p className="text-xs text-text-tertiary mt-1">
                Register this URL in your ClickSend account settings to receive delivery receipts.
              </p>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSaveClicksend} data-testid="save-clicksend-btn">
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
              <Button variant="outline" onClick={handleTestClicksend} disabled={clicksendTesting}>
                <RefreshCw className={`w-4 h-4 mr-2 ${clicksendTesting ? 'animate-spin' : ''}`} />
                Test Connection
              </Button>
            </div>
          </SettingSection>
        </TabsContent>

        {/* Gamma AI Tab */}
        <TabsContent value="gamma" className="space-y-6 mt-6">
          <SettingSection
            title="Gamma AI Configuration"
            description="Configure AI-powered report generation"
          >
            <div className="flex items-center justify-between mb-4">
              <ConnectionStatus connected={gammaConnected} testing={gammaTesting} />
            </div>

            <div>
              <Label>API Key</Label>
              <div className="relative">
                <Input
                  type={showGammaKey ? 'text' : 'password'}
                  value={gammaConfig.api_key}
                  onChange={(e) => setGammaConfig({...gammaConfig, api_key: e.target.value})}
                  className="bg-bg-tertiary border-border-default pr-10"
                  data-testid="gamma-api-key"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0"
                  onClick={() => setShowGammaKey(!showGammaKey)}
                >
                  {showGammaKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div>
              <Label>Default Template ID (Optional)</Label>
              <Input
                value={gammaConfig.template_id}
                onChange={(e) => setGammaConfig({...gammaConfig, template_id: e.target.value})}
                className="bg-bg-tertiary border-border-default"
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSaveGamma} data-testid="save-gamma-btn">
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
              <Button variant="outline" onClick={handleTestGamma} disabled={gammaTesting}>
                <RefreshCw className={`w-4 h-4 mr-2 ${gammaTesting ? 'animate-spin' : ''}`} />
                Test Connection
              </Button>
            </div>
          </SettingSection>
        </TabsContent>

        {/* Grok AI Tab */}
        <TabsContent value="grok" className="space-y-6 mt-6">
          <SettingSection
            title="Grok API Configuration"
            description="Configure the xAI Grok API for automated story generation"
          >
            <div className="flex items-center justify-between mb-4">
              <ConnectionStatus connected={grokConnected} testing={grokTesting} />
            </div>

            <div>
              <Label>API Key</Label>
              <div className="relative">
                <Input
                  type={showGrokKey ? 'text' : 'password'}
                  value={grokConfig.api_key}
                  onChange={(e) => setGrokConfig({...grokConfig, api_key: e.target.value})}
                  className="bg-bg-tertiary border-border-default pr-10"
                  data-testid="grok-api-key"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0"
                  onClick={() => setShowGrokKey(!showGrokKey)}
                >
                  {showGrokKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div>
              <Label>API Base URL</Label>
              <Input
                value={grokConfig.api_base_url}
                onChange={(e) => setGrokConfig({...grokConfig, api_base_url: e.target.value})}
                placeholder="https://api.x.ai/v1"
                className="bg-bg-tertiary border-border-default font-mono text-sm"
              />
            </div>

            <div>
              <Label>Default Model</Label>
              <Input
                value={grokConfig.default_model}
                onChange={(e) => setGrokConfig({...grokConfig, default_model: e.target.value})}
                placeholder="grok-3"
                className="bg-bg-tertiary border-border-default font-mono"
              />
            </div>

            <div className="bg-slate-50 border border-slate-200 p-4 rounded-lg">
              <p className="text-sm text-text-secondary">
                Grok is used exclusively for the <span className="text-accent-primary">Automated Stories</span> feature. Document intelligence extraction uses the Claude API (configured separately via code).
              </p>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSaveGrok} data-testid="save-grok-btn">
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
              <Button variant="outline" onClick={handleTestGrok} disabled={grokTesting}>
                <RefreshCw className={`w-4 h-4 mr-2 ${grokTesting ? 'animate-spin' : ''}`} />
                Test Connection
              </Button>
            </div>
          </SettingSection>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6 mt-6">
          <SettingSection
            title="Publication Analytics"
            description="Configure web analytics per publication (Matomo or GA4)"
          >
            <div className="flex items-center justify-between mb-4">
              <ConnectionStatus connected={analyticsConnected} testing={analyticsTesting} />
            </div>

            <div>
              <Label>Publication</Label>
              <Select value={selectedPublication} onValueChange={setSelectedPublication}>
                <SelectTrigger className="bg-bg-tertiary border-border-default">
                  <SelectValue placeholder="Select publication" />
                </SelectTrigger>
                <SelectContent>
                  {publications.map((pub) => (
                    <SelectItem key={pub.id} value={pub.id}>{pub.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Analytics Provider</Label>
              <Select value={analyticsConfig.provider || 'none'} onValueChange={(v) => setAnalyticsConfig({...analyticsConfig, provider: v === 'none' ? '' : v})}>
                <SelectTrigger className="bg-bg-tertiary border-border-default">
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="matomo">Matomo</SelectItem>
                  <SelectItem value="ga4">Google Analytics 4</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {analyticsConfig.provider === 'matomo' && (
              <>
                <div>
                  <Label>Matomo URL</Label>
                  <Input
                    value={analyticsConfig.matomo_url}
                    onChange={(e) => setAnalyticsConfig({...analyticsConfig, matomo_url: e.target.value})}
                    placeholder="https://analytics.example.com"
                    className="bg-bg-tertiary border-border-default"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Site ID</Label>
                    <Input
                      value={analyticsConfig.matomo_site_id}
                      onChange={(e) => setAnalyticsConfig({...analyticsConfig, matomo_site_id: e.target.value})}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                  <div>
                    <Label>Auth Token</Label>
                    <Input
                      type="password"
                      value={analyticsConfig.matomo_auth_token}
                      onChange={(e) => setAnalyticsConfig({...analyticsConfig, matomo_auth_token: e.target.value})}
                      className="bg-bg-tertiary border-border-default"
                    />
                  </div>
                </div>
              </>
            )}

            {analyticsConfig.provider === 'ga4' && (
              <div>
                <Label>GA4 Property ID</Label>
                <Input
                  value={analyticsConfig.ga4_property_id}
                  onChange={(e) => setAnalyticsConfig({...analyticsConfig, ga4_property_id: e.target.value})}
                  placeholder="e.g., 123456789"
                  className="bg-bg-tertiary border-border-default"
                />
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={handleSaveAnalytics} data-testid="save-analytics-btn">
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
              <Button variant="outline" onClick={handleTestAnalytics} disabled={analyticsTesting || !selectedPublication}>
                <RefreshCw className={`w-4 h-4 mr-2 ${analyticsTesting ? 'animate-spin' : ''}`} />
                Test Connection
              </Button>
            </div>
          </SettingSection>
        </TabsContent>

        {/* Branding Tab */}
        <TabsContent value="branding" className="space-y-6 mt-6">
          <SettingSection
            title="Report Branding"
            description="Customize PDF exports and shared reports"
          >
            <div>
              <Label>Company Logo</Label>
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 bg-slate-100 border border-slate-200 rounded-lg flex items-center justify-center text-text-tertiary">
                  Logo
                </div>
                <Button variant="outline">Upload Logo</Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Brand Color</Label>
                <div className="flex gap-2">
                  <Input
                    value="#3B82F6"
                    className="bg-bg-tertiary border-border-default"
                    readOnly
                  />
                  <div className="w-10 h-10 rounded bg-accent-primary" />
                </div>
              </div>
            </div>

            <div>
              <Label>Default Footer Text</Label>
              <Input
                placeholder="Prepared by QuantumCast"
                className="bg-bg-tertiary border-border-default"
              />
            </div>

            <Button data-testid="save-branding-btn">
              <Save className="w-4 h-4 mr-2" />
              Save Branding
            </Button>
          </SettingSection>

          <SettingSection
            title="Social Media"
            description="Connect social media accounts for future integration"
          >
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-slate-50 border-slate-200 p-4">
                <p className="font-medium text-text-primary">Instagram</p>
                <Badge className="mt-2">Coming Soon</Badge>
              </Card>
              <Card className="bg-slate-50 border-slate-200 p-4">
                <p className="font-medium text-text-primary">LinkedIn</p>
                <Badge className="mt-2">Coming Soon</Badge>
              </Card>
              <Card className="bg-slate-50 border-slate-200 p-4">
                <p className="font-medium text-text-primary">X (Twitter)</p>
                <Badge className="mt-2">Coming Soon</Badge>
              </Card>
              <Card className="bg-slate-50 border-slate-200 p-4">
                <p className="font-medium text-text-primary">Facebook</p>
                <Badge className="mt-2">Coming Soon</Badge>
              </Card>
            </div>
          </SettingSection>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default Settings;